import { FC, useState, useMemo } from "react";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import {
  NotificationsOutlined,
  NotificationsOffOutlined,
  EditNotificationsOutlined,
} from "@mui/icons-material";
import { makeSxStyles } from "ui";
import {
  getRouteNotificationState,
  getGlobalNotificationStateFromAPI,
} from "../../../shared/utils/route-notification-utils";
import {
  AdminGetRoutesResDto,
  AdminGetDateRouteNotificationsResDto,
} from "service/api/estabrookSchemas";
import { toast } from "react-toastify";

export type NotificationState = "active" | "disabled" | "custom";

interface NotificationBellProps {
  routeId?: string;
  routeName?: string;
  routeData?: AdminGetRoutesResDto;
  globalSettings?: AdminGetDateRouteNotificationsResDto;
  onSendAll?: (routeId?: string, routeName?: string) => void;
  onMuteAll?: (routeId?: string, routeName?: string) => void;
  onCustomize?: (routeId?: string, routeName?: string) => void;
  variant?: "route" | "global";
}

export const NotificationBell: FC<NotificationBellProps> = ({
  routeId,
  routeName,
  routeData,
  globalSettings,
  onSendAll,
  onMuteAll,
  onCustomize,
  variant = "route",
}) => {
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isGlobal = variant === "global";

  const actualState = useMemo(() => {
    if (isGlobal && globalSettings) {
      return getGlobalNotificationStateFromAPI(globalSettings);
    }
    if (routeData) return getRouteNotificationState(routeData, globalSettings);
    return "disabled";
  }, [isGlobal, globalSettings, routeData]);
  const menuId = isGlobal
    ? "global-notification-menu"
    : "route-notification-menu";
  const buttonId = isGlobal
    ? "global-notification-button"
    : "route-notification-button";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const checkIfActionAllowed = () => {
    if (
      !isGlobal &&
      getGlobalNotificationStateFromAPI(globalSettings) === "disabled"
    ) {
      toast.error(
        "Cannot modify route notifications while global notifications are muted. Please disable the global mute toggle first.",
      );
      handleClose();
      return false;
    }
    return true;
  };

  const handleSendAll = () => {
    if (!checkIfActionAllowed()) return;
    handleClose();
    onSendAll?.(routeId, routeName);
  };

  const handleMuteAll = () => {
    if (!checkIfActionAllowed()) return;
    handleClose();
    onMuteAll?.(routeId, routeName);
  };

  const handleCustomize = () => {
    if (!checkIfActionAllowed()) return;
    handleClose();
    onCustomize?.(routeId, routeName);
  };

  const getBellIcon = () => {
    switch (actualState) {
      case "active":
        return <NotificationsOutlined sx={styles.activeIcon} />;
      case "disabled":
        return <NotificationsOffOutlined sx={styles.disabledIcon} />;
      case "custom":
        return <EditNotificationsOutlined sx={styles.customIcon} />;
      default:
        return <NotificationsOutlined sx={styles.activeIcon} />;
    }
  };

  return (
    <>
      <Tooltip
        title="Customize plow status notifications"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: "common.white",
              color: "common.black",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.12)",
              "& .MuiTooltip-arrow": {
                color: "common.white",
              },
            },
          },
        }}
      >
        <IconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            padding: "4px",
            "& .MuiSvgIcon-root": {
              fontSize: "18px",
            },
          }}
        >
          {getBellIcon()}
        </IconButton>
      </Tooltip>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": buttonId,
        }}
      >
        <MenuItem
          onClick={handleSendAll}
          disabled={
            !isGlobal &&
            getGlobalNotificationStateFromAPI(globalSettings) === "custom"
          }
        >
          <NotificationsOutlined sx={{ mr: 1.5 }} />
          Enable All Notifications
        </MenuItem>
        <MenuItem onClick={handleMuteAll}>
          <NotificationsOffOutlined sx={{ mr: 1.5 }} />
          Mute All Notifications
        </MenuItem>
        <MenuItem onClick={handleCustomize}>
          <EditNotificationsOutlined sx={{ mr: 1.5 }} />
          Customize
        </MenuItem>
      </Menu>
    </>
  );
};

const useStyles = makeSxStyles(({ palette }) => ({
  activeIcon: {
    color: palette.success.main,
  },
  disabledIcon: {
    color: palette.grey[300],
  },
  customIcon: {
    color: palette.grey[100],
  },
}));
